import { useMemo, useState } from 'react'
import classes from './ChatWidget.module.scss'
import { ChatCommissionRates, CommissionMode } from './types'
import {
  bestBanks,
  formatInstallmentLabel,
  getAvailableCompanies,
  getAvailablePaymentTypes,
  getDefaultPaymentTypeCode,
  groupCommissionRates,
} from './commissionRatesService'
import { formatChatRate } from './chatFormatters'
import { IconChevron } from './icons'

interface Props {
  rates: ChatCommissionRates
}

const MODES: { value: CommissionMode; label: string }[] = [
  { value: 'best', label: 'En uygun' },
  { value: 'all', label: 'Tümü' },
]

/**
 * Komisyon oranları kartı. Tüm veri payload'da olduğu için sekme/chip/mod geçişleri saf state
 * değişimidir — kart kendi isteğini atmaz, geçmişten geri yüklenen balonlarda da çalışır.
 */
export default function CommissionRatesCard({ rates }: Props) {
  const companies = useMemo(() => getAvailableCompanies(rates.rates, rates.companies, rates.paymentTypes), [rates])
  const [companyCode, setCompanyCode] = useState(() => companies[0]?.code ?? '')
  const [paymentTypeCode, setPaymentTypeCode] = useState('')
  const [mode, setMode] = useState<CommissionMode>(rates.mode)
  const [openInstallment, setOpenInstallment] = useState<number | null>(null)

  const paymentTypes = useMemo(
    () => getAvailablePaymentTypes(rates.rates, rates.paymentTypes, companyCode),
    [rates, companyCode]
  )
  // Seçili tip yeni şirkette yoksa varsayılana düşülür; state'i effect ile sıfırlamaya gerek yok.
  const activeTypeCode = paymentTypes.some(paymentType => paymentType.code === paymentTypeCode)
    ? paymentTypeCode
    : getDefaultPaymentTypeCode(paymentTypes)

  const groups = useMemo(
    () => groupCommissionRates(rates.rates, companyCode, activeTypeCode),
    [rates, companyCode, activeTypeCode]
  )

  // Etiket sözlüğü (Companies/PaymentTypes) hiçbir oran satırıyla eşleşmiyorsa çizecek bir şey yok.
  if (companies.length === 0 || paymentTypes.length === 0) return null

  const selectCompany = (code: string) => {
    setCompanyCode(code)
    setOpenInstallment(null)
  }

  const selectPaymentType = (code: string) => {
    setPaymentTypeCode(code)
    setOpenInstallment(null)
  }

  return (
    <div className={classes.commissionCard}>
      <div className={classes.commissionHead}>
        <span className={classes.commissionTitle}>Kredi Kartı Komisyon Oranları</span>
        <div className={classes.commissionModes} role='group' aria-label='Görünüm'>
          {MODES.map(item => (
            <button
              key={item.value}
              type='button'
              className={`${classes.commissionMode} ${mode === item.value ? classes.commissionModeOn : ''}`}
              aria-pressed={mode === item.value}
              onClick={() => setMode(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tek şirket/tip olsa da satır verinin kapsamını etiketler; gizlemek "hangi şirket/tip"
          bilgisini kaybettirip önceki seçimle karışmasına yol açabilir (bkz. review notu). */}
      <div className={classes.commissionTabs} aria-label='Şirket'>
        {companies.map(company => (
          <button
            key={company.code}
            type='button'
            aria-pressed={company.code === companyCode}
            className={`${classes.commissionTab} ${company.code === companyCode ? classes.commissionTabOn : ''}`}
            onClick={() => selectCompany(company.code)}
          >
            {company.name}
          </button>
        ))}
      </div>

      <div className={classes.commissionChips}>
        {paymentTypes.map(paymentType => (
          <button
            key={paymentType.code}
            type='button'
            className={`${classes.commissionChip} ${
              paymentType.code === activeTypeCode ? classes.commissionChipOn : ''
            }`}
            aria-pressed={paymentType.code === activeTypeCode}
            onClick={() => selectPaymentType(paymentType.code)}
          >
            {paymentType.name}
          </button>
        ))}
      </div>

      <div className={classes.commissionBody}>
        {groups.map(group =>
          mode === 'best' ? (
            // Eşit en uygun oranlı bankaların HEPSİ listelenir; oran satırda bir kez yazılır.
            <div key={group.installment} className={classes.commissionBestRow}>
              <span className={classes.commissionBestLabel}>{formatInstallmentLabel(group.installment)}</span>
              <span className={classes.commissionBestBanks}>
                {bestBanks(group).map(bank => (
                  <span key={bank.bankName} className={classes.commissionBestBank}>
                    {bank.bankName}
                  </span>
                ))}
              </span>
              <span className={classes.commissionRate}>{formatChatRate(group.banks[0].interestRate)}</span>
            </div>
          ) : (
            <div key={group.installment} className={classes.commissionBlock}>
              <button
                type='button'
                className={`${classes.commissionBlockHead} ${
                  openInstallment === group.installment ? classes.commissionBlockHeadOn : ''
                }`}
                aria-expanded={openInstallment === group.installment}
                onClick={() => setOpenInstallment(openInstallment === group.installment ? null : group.installment)}
              >
                <span className={classes.commissionBlockLabel}>{formatInstallmentLabel(group.installment)}</span>
                <span className={classes.commissionBlockMeta}>
                  en uygun <span className={classes.commissionRate}>{formatChatRate(group.banks[0].interestRate)}</span>
                  {` · ${group.banks.length} banka`}
                </span>
                <span
                  className={`${classes.commissionChevron} ${
                    openInstallment === group.installment ? classes.commissionChevronUp : ''
                  }`}
                >
                  <IconChevron />
                </span>
              </button>

              {openInstallment === group.installment && (
                <div className={classes.commissionBankList}>
                  {group.banks.map((bank, index) => (
                    <div key={bank.bankName} className={classes.commissionBankRow}>
                      <span className={classes.commissionRank}>{index + 1}</span>
                      <span className={classes.commissionBankName}>{bank.bankName}</span>
                      <span className={index === 0 ? classes.commissionRate : undefined}>
                        {formatChatRate(bank.interestRate)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        )}
      </div>

      {rates.note && <p className={classes.commissionNote}>{rates.note}</p>}
    </div>
  )
}
